(() => {
  'use-strict';

  /**
   * A simple animated and accessible accordion
   *
   * available options :
   * - data-animated="true" : enable transition animation
   * - data-close-siblings="true" : close siblings accordions  of js-accordion-group
   * - data-force-visible="desktop" : trigger click to open depending on breakpoint
   */

  const getSiblings = (e, selector = '*') => {
    // for collecting siblings
    const siblings = [];
    // if no parent, return no sibling
    if (!e.parentNode) {
      return siblings;
    }
    // first child of the parent node
    let sibling = e.parentNode.firstChild;

    // collecting siblings
    while (sibling) {
      if (sibling.nodeType === 1 && sibling !== e && sibling.matches(selector)) {
        siblings.push(sibling);
      }
      sibling = sibling.nextSibling;
    }
    return siblings;
  };

  class Accordion {
    constructor(domNode) {
      this.rootEl = domNode;
      this.buttonEl = this.rootEl.querySelector('button[aria-expanded]');
      this.groupEl = this.rootEl.closest('.js-accordion-group');
      this.closeSiblings = this.groupEl.getAttribute('data-close-siblings') === 'true';
      this.forceVisibleOn = this.groupEl.getAttribute('data-force-visible');
      this.animated = this.groupEl.getAttribute('data-animated') === 'true';
      this.siblingsElts = getSiblings(this.rootEl, '.js-accordion');

      const controlsId = this.buttonEl.getAttribute('aria-controls');
      this.contentEl = document.getElementById(controlsId);

      this.open = this.buttonEl.getAttribute('aria-expanded') === 'true';

      this.openEvent = new CustomEvent('accordionOpen');
      this.openEndEvent = new CustomEvent('accordionOpenEnd');
      this.closeEvent = new CustomEvent('accordionClose');
      this.closeEndEvent = new CustomEvent('accordionCloseEnd');

      // add event listeners
      this.buttonEl.addEventListener('click', this.onButtonClick.bind(this));
      if (this.animated) {
        this.contentEl.addEventListener('animationend', this.onAnimationEnd.bind(this));
      }

      if (this.forceVisibleOn !== null) {
        document.addEventListener('breakpointChange', (event) => {
          if (!(event.detail.breakpoints.includes(this.forceVisibleOn))) {
            // breakpoints before selected breakpoint
            // close by default
            this.toggle(false, false);
          } else {
            // selected breakpoint and up
            // Force open
            this.toggle(true, false);
          }
        }, true);
      }
    }

    onButtonClick() {
      this.toggle(!this.open, this.animated);
    }

    onAnimationEnd() {
      if (this.open) {
        this.onOpenEnd();
      } else {
        this.onCloseEnd();
      }
    }

    onOpenEnd() {
      this.contentEl.classList.remove('-isOpening');
      this.contentEl.classList.remove('-wasClosed');
      this.rootEl.dispatchEvent(this.openEndEvent);
    }

    onClose() {
      this.contentEl.setAttribute('hidden', '');
      this.rootEl.dispatchEvent(this.closeEndEvent);
    }

    onCloseEnd() {
      this.onClose();
      this.contentEl.classList.remove('-isClosing');
      this.contentEl.classList.remove('-wasOpened');
    }

    toggle(open, animated) {
      // don't do anything if the open state doesn't change
      if (open === this.open) {
        return;
      }

      // update the internal state
      this.open = open;
      // handle DOM updates
      this.buttonEl.setAttribute('aria-expanded', `${open}`);
      if (open) {
        this.contentEl.removeAttribute('hidden');
        this.rootEl.dispatchEvent(this.openEvent);
        if (animated) {
          this.contentEl.classList.add('-temporaryHidden');
          this.contentEl.style.setProperty('--accordion-panel-height', `${this.contentEl.scrollHeight}px`);
          this.contentEl.classList.remove('-temporaryHidden');
          this.contentEl.classList.add('-wasClosed');
          this.contentEl.classList.add('-isOpening');
        }

        if (this.closeSiblings) {
          this.siblingsElts.forEach((sibling) => {
            sibling.accordion.toggle(false, this.animated);
          });
        }
        this.groupEl.classList.add('-childrenAccordionOpened');
      } else {
        this.rootEl.dispatchEvent(this.closeEvent);
        if (animated) {
          this.contentEl.style.setProperty('--accordion-panel-height', `${this.contentEl.scrollHeight}px`);
          this.contentEl.classList.add('-wasOpened');
          this.contentEl.classList.add('-isClosing');
        } else {
          this.onClose();
        }

        let otherSiblingOpened = false;
        this.siblingsElts.every((sibling) => {
          if (sibling.accordion.open) {
            otherSiblingOpened = true;
            return false;
          }
          return true;
        });
        this.groupEl.classList.toggle('-childrenAccordionOpened', otherSiblingOpened);
      }
    }

    // Add public open and close methods for convenience
    open() {
      this.toggle(true, this.animated);
    }

    close() {
      this.toggle(false, this.animated);
    }
  }

  function init(elements) {
    elements.forEach((element) => {
      // Check that the target is not already initialized
      if ('init' in element.dataset) {
        return;
      }
      element.dataset.init = true;

      element.accordion = new Accordion(element);
    });
  }

  Drupal.behaviors.toggleAccordions = {
    attach: () => {
      const elements = document.querySelectorAll('.js-accordion');
      if (elements.length !== 0) {
        elements.forEach((element, index) => {
          const button = element.querySelector('button');
          const panel = element.querySelector('.js-accordion-panel');

          if (!button || !panel) {
            return; // skip to next
          }

          const baseId = button.getAttribute('id');
          const basePanel = button.getAttribute('aria-controls');

          const newBaseId = `${baseId}-${index}`;
          const newBasePanel = `${basePanel}-${index}`;

          button.setAttribute('id', newBaseId);
          button.setAttribute('aria-controls', newBasePanel);

          panel.setAttribute('id', newBasePanel);
          panel.setAttribute('aria-labelledby', newBaseId);
        });
        init(elements);
      }
    },
  };
})();
